import { Layout, Button, Menu, Drawer } from '@arco-design/web-react';
import { LogoutOutlined } from '@ant-design/icons';
import { BrowserRouter as Router, Switch, Route, Link } from 'react-router-dom';
import routes, { SUB_MENU, SubMenuTitle } from '../../routes';
import { createContext, Suspense, useContext, useMemo, useState } from 'react';
import './index.css';
import styled from 'styled-components';
import Loading from '../components/Loading';
import { PageHeader } from '../components/Layout';
import { RedirectComponent } from './RedirectComponent';
import { useLargeScreen } from '../../hooks/useLargeScreen';

const { Content } = Layout;

interface PropsType {
  username: string;
  accesses: string[];
  logOut: () => void;
}

const StyledAntdContent = styled(Content)`
  width: 100%;
  height: calc(100vh - 50px);
  box-sizing: border-box;
  display: flex;
  background-color: #fff;
`;

const RouteContent = styled.div`
  flex: 1;
  overflow: scroll;
`;

const StyledMenu = styled(Menu)`
  width: 200px;
  flex-shrink: 0;
  background-color: var(--color-fill-1);

  .arco-menu-inline-header {
    background-color: var(--color-fill-1);
  }

  .arco-menu-item {
    background-color: var(--color-fill-1);
    .menu-icon {
      display: inline-block;
      width: 32px;
      height: 32px;
      font-size: 20px;
      line-height: 32px;
      svg {
        vertical-align: middle;
      }
    }
  }
  .arco-menu-item:hover {
    background-color: var(--color-fill-3);
    color: rgba(var(--primary-6));
    font-weight: 500;
    .menu-icon {
      svg {
        color: rgba(var(--primary-6));
      }
    }
  }
  .arco-menu-selected {
    background-color: var(--color-fill-3) !important;
    color: rgba(var(--primary-6));
    font-weight: 500;
    .menu-icon {
      svg {
        color: rgba(var(--primary-6));
      }
    }
  }
`;

type GlobalContextType = {
  isSearchMode: boolean;
  setIsSearchMode: (value: boolean) => void;
};
const GlobalContext = createContext<GlobalContextType>({
  isSearchMode: false,
  setIsSearchMode: () => {},
});

export const useGlobalContext = () => useContext(GlobalContext);

function Home({ username, accesses, logOut }: PropsType) {
  const privateRoutes = routes.filter((route) =>
    route.accesses.some((access) => accesses.includes(access))
  );
  const subMenus = Array.from(
    new Set(privateRoutes.map((route) => route.subMenu).filter(Boolean))
  ) as SUB_MENU[];

  const visibleMenuItemsCount = privateRoutes.filter(
    (route) => !route.hiddenInNav
  ).length;

  const currentRoute = privateRoutes.find(
    (route) => route.path === window.location.pathname
  );
  const menu = useMemo(
    () => (
      <Menu>
        <Menu.Item key="logout">
          <LogoutOutlined />
          <Button type="text" onClick={logOut}>
            登出
          </Button>
        </Menu.Item>
      </Menu>
    ),
    [logOut]
  );
  const isLargeScreen = useLargeScreen();
  const [isMenuDrawerVisible, setMenuDrawerVisible] = useState(false);
  const [isSearchMode, setIsSearchMode] = useState<boolean>(false);

  const SideMenuBar = (
    <StyledMenu
      defaultOpenKeys={[currentRoute?.subMenu || SUB_MENU.FACTORY]}
      defaultSelectedKeys={[currentRoute?.name || 'CONTRACT']}
    >
      {subMenus.map((subMenu) => (
        <Menu.SubMenu key={subMenu} title={SubMenuTitle[subMenu]}>
          {privateRoutes
            .filter((item) => item.subMenu === subMenu)
            .map((route, index) => (
              <Link key={index} to={route.path}>
                <Menu.Item key={route.name} title={route.title}>
                  {route.icon ? (
                    <span className="menu-icon">{route.icon}</span>
                  ) : null}
                  {route.title}
                </Menu.Item>
              </Link>
            ))}
        </Menu.SubMenu>
      ))}
    </StyledMenu>
  );

  return (
    <GlobalContext.Provider
      value={{
        isSearchMode,
        setIsSearchMode: (value: boolean) => setIsSearchMode(value),
      }}
    >
      <Router>
        <Layout
          style={{
            height: '100vh',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <RedirectComponent privateRoutes={privateRoutes} />
          <PageHeader
            userName={username}
            menu={menu}
            isMenuIconShown={!isLargeScreen}
            onMenuIconClick={() => setMenuDrawerVisible(true)}
            isSearchIconShown={false}
            onSearchIconClick={() => setIsSearchMode(true)}
          />
          <StyledAntdContent>
            {visibleMenuItemsCount > 1 && isLargeScreen ? (
              SideMenuBar
            ) : (
              <Drawer
                visible={isMenuDrawerVisible}
                onCancel={() => setMenuDrawerVisible(false)}
                placement="left"
                width={180}
                bodyStyle={{ padding: 0 }}
                footer={null}
              >
                <Menu
                  defaultOpenKeys={[currentRoute?.subMenu || SUB_MENU.FACTORY]}
                  defaultSelectedKeys={[currentRoute?.name || 'CONTRACT']}
                >
                  {subMenus.map((subMenu) => (
                    <Menu.SubMenu key={subMenu} title={SubMenuTitle[subMenu]}>
                      {privateRoutes
                        .filter((item) => item.subMenu === subMenu)
                        .map((route, index) => (
                          <Link
                            key={index}
                            to={route.path}
                            onClick={() => {
                              setMenuDrawerVisible(false);
                            }}
                          >
                            <Menu.Item key={route.name} title={route.title}>
                              {route.icon ? <span>{route.icon}</span> : null}
                              {route.title}
                            </Menu.Item>
                          </Link>
                        ))}
                    </Menu.SubMenu>
                  ))}
                </Menu>
              </Drawer>
            )}
            <RouteContent>
              <Suspense fallback={<Loading />}>
                <Switch>
                  {privateRoutes.map((route, index) => (
                    <Route
                      key={index}
                      path={route.path}
                      exact={route.exact}
                      component={route.main}
                    />
                  ))}
                </Switch>
              </Suspense>
            </RouteContent>
          </StyledAntdContent>
        </Layout>
      </Router>
    </GlobalContext.Provider>
  );
}

export default Home;
